import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Import a list" — a CSV of numbers the account already keeps, added to Do not call in one go.
 *
 * HOW THE FILE TRAVELS (design §15.5.6, DD-6, C16). There is no upload operation: the file's
 * TEXT, read with `File.text()`, is the `uploadHandle`, and the slice sends it as `source_text`.
 * Two consequences this panel is built around:
 *
 *   1. **The size is checked before the file is read.** A file over 5 MiB is refused here, with
 *      the reason, and neither `File.text()` nor any request runs. The API's own ceiling stays
 *      authoritative — a `413 payload_too_large` is still rendered — but nobody should wait for
 *      an 11 MiB request to learn what the browser already knew.
 *   2. **Nothing is sent until "Import" is pressed.** Choosing a file only reads it, so a wrong
 *      file can be swapped without a half-import to undo.
 *
 * The outcome names all three counts, because "added 1,204" on a 1,300-row file is only honest
 * beside "96 already on the list".
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import {
  DO_NOT_CALL_FILE_MAX_BYTE,
  FILE_EMPTY_TEXT,
  FILE_TOO_LARGE_TEXT,
  FILE_UNREADABLE_TEXT,
  IMPORT_HINT_TEXT,
  importOutcomeText,
  refusalText,
  unreadableMoreText,
  unreadableRowText,
} from "./do-not-call-copy.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY } from "../../api/query.js";

export function DoNotCallImportPanel({ onClose }: { readonly onClose: () => void }): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [fileName, setFileName] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [fileRefusal, setFileRefusal] = usePortalMessage();

  const importing = useMutation({
    mutationFn: (text: string) => gateway.importDoNotCallEntries(text),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY.doNotCall });
    },
  });

  const attached = sourceText !== "";
  const moreUnreadable =
    importing.data === undefined
      ? null
      : unreadableMoreText(importing.data.unreadableRow.length, importing.data.unreadableCount);

  return (
    <div className="c-panel dnc-panel">
      <p className="dnc-panel-title">{portalText("finance", "m1029")}</p>

      <label className="c-drop dnc-drop" data-attached={attached}>
        <input
          type="file"
          accept=".csv,text/csv"
          className="c-visually-hidden"
          aria-label={portalText("finance", "m1030")}
          onChange={(event) => {
            const chosen = event.currentTarget.files?.[0];
            importing.reset();
            setSourceText("");
            setFileRefusal(null);
            setFileName(chosen === undefined ? "" : chosen.name);
            if (chosen === undefined) return;
            if (chosen.size > DO_NOT_CALL_FILE_MAX_BYTE) {
              // Refused before it is read: no `File.text()`, no request.
              setFileRefusal(() => (FILE_TOO_LARGE_TEXT()));
              return;
            }
            chosen.text().then(
              (text) => {
                // Review #252: an empty file left Import disabled with no reason given.
                if (text.replace(/^﻿/, "").trim() === "") {
                  setFileRefusal(() => (FILE_EMPTY_TEXT()));
                  return;
                }
                setSourceText(text);
              },
              () => {
                setFileRefusal(() => (FILE_UNREADABLE_TEXT()));
              },
            );
          }}
        />
        <span className="c-drop-name">
          {fileName !== "" ? fileName : portalText("finance", "m1031")}
        </span>
        <span className="c-drop-hint">{IMPORT_HINT_TEXT()}</span>
      </label>

      {fileRefusal === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {fileRefusal}
        </p>
      )}

      {importing.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {refusalText("import", importing.error)}
        </p>
      ) : null}

      {importing.data === undefined ? null : (
        <div role="status">
          <p className="c-note">
            {importOutcomeText(
              importing.data.addedCount,
              importing.data.alreadyPresentCount,
              importing.data.unreadableCount,
            )}
          </p>
          {/* Which lines, so they can be found and fixed (review #252). */}
          {importing.data.unreadableRow.length === 0 ? null : (
            <ul className="dnc-unreadable" aria-label={portalText("finance", "m1032")}>
              {importing.data.unreadableRow.map((one) => (
                <li key={one.line}>{unreadableRowText(one)}</li>
              ))}
              {moreUnreadable === null ? null : <li>{moreUnreadable}</li>}
            </ul>
          )}
        </div>
      )}

      <div className="c-row dnc-panel-foot">
        <Button variant="secondary" onClick={onClose}>
          {importing.data === undefined ? portalText("finance", "m0106") : portalText("finance", "m0597")}
        </Button>
        <Button
          variant="primary"
          disabled={!attached || importing.isPending || importing.data !== undefined}
          onClick={() => {
            importing.mutate(sourceText);
          }}
        >
          {importing.isPending ? portalText("finance", "m1033") : portalText("finance", "m1034")}
        </Button>
      </div>
    </div>
  );
}
